/**
 * Semantic line breaks (S58, T16 in docs/13-typografie.md): a text that needs several lines breaks
 * where its sense breaks – after a full stop, a dash or a comma, before „mit“, „und“ or „dazu“ – and
 * never inside what belongs together („winterlich gefülltes / Schweinefilet“), and no line stays
 * short. Never more lines than breaking at the width: the number of lines is given, only the places
 * change.
 *
 * The words come measured (PDF and Word measure with their fonts); `semanticPlan` picks the line
 * starts with the least cost: a cost for each place it breaks at, a cost for each line's free room.
 */

import type { Lang } from "./translation.ts";

/** A word as measured: its width and the width of the space before it. */
export interface BreakWord {
  text: string;
  width: number;
  space: number;
}

/** „left“: a short last line costs; „balance“ (headings, centred text): every line about as long as the others. */
export type BreakMode = "left" | "balance";

/**
 * The small words of each language of the card (S36): those that lead into what follows (articles,
 * prepositions, conjunctions – a line never ends with them), those a new line begins well with
 * (Martin: „Satzzeichen, ‚und‘ oder ‚mit‘“), and the prepositions, which open a part a little less
 * clearly. All languages count in every text: a German card has „Spaghetti alla vongole“, „pan y
 * tomate“. **A new language of the card needs its words here** (docs/15-sprachen.md § 8); the type
 * asks for them, as for the card's fixed words.
 */
const SMALL_WORDS: Record<"de" | Lang, { leading: string[]; opening: string[]; prepositions: string[] }> = {
  de: {
    leading: ["mit", "und", "oder", "sowie", "dazu", "an", "am", "auf", "aus", "bei", "beim", "für", "in", "im", "ins", "nach", "von", "vom", "zu", "zum", "zur", "über", "unter", "vor", "ohne", "um", "durch", "gegen", "nebst", "samt", "der", "die", "das", "den", "dem", "des", "ein", "eine", "einer", "einem", "einen", "eines", "kein", "keine", "je", "pro", "ab", "bis"],
    opening: ["mit", "und", "oder", "sowie", "dazu"],
    prepositions: ["an", "auf", "aus", "bei", "in", "im", "nach", "von", "vom", "zum", "zur", "über", "unter", "ohne"],
  },
  en: { leading: ["with", "and", "or", "of", "the", "a", "an", "on", "in", "to", "from", "&", "+"], opening: ["with", "and", "or"], prepositions: ["on", "in", "from"] },
  es: { leading: ["con", "y", "e", "o", "u", "de", "del", "al", "a", "en", "el", "la", "los", "las", "un", "una", "sin", "por", "para", "sobre"], opening: ["con", "y", "o"], prepositions: ["en", "sobre", "sin"] },
  fr: { leading: ["avec", "et", "ou", "de", "du", "des", "à", "au", "aux", "le", "la", "les", "un", "une", "en", "sur", "sans", "pour"], opening: ["avec", "et", "ou"], prepositions: ["sur", "sans"] },
  it: { leading: ["con", "e", "o", "di", "del", "della", "dello", "dei", "delle", "al", "alla", "allo", "ai", "alle", "a", "in", "il", "lo", "la", "i", "gli", "le", "un", "una", "uno", "su", "senza", "per", "da"], opening: ["con", "e", "o"], prepositions: ["su", "senza"] },
  nl: { leading: ["met", "en", "of", "van", "de", "het", "een", "in", "op", "aan", "bij", "uit", "zonder", "voor", "naar"], opening: ["met", "en", "of"], prepositions: ["op", "aan", "bij", "uit", "zonder"] },
  pt: { leading: ["com", "e", "ou", "de", "do", "da", "dos", "das", "ao", "à", "a", "o", "os", "as", "um", "uma", "em", "no", "na", "nos", "nas", "sem", "para", "por"], opening: ["com", "e", "ou"], prepositions: ["em", "sem"] },
  // Turkish puts „with“ after the word, as an ending or a postposition („pilav ile“, „yoğurtlu“): only the conjunctions lead.
  tr: { leading: ["ve", "veya", "ya", "bir"], opening: ["ve", "veya"], prepositions: [] },
};
const all = (part: "leading" | "opening" | "prepositions") => Object.values(SMALL_WORDS).flatMap((w) => w[part]).map((w) => w.toLocaleLowerCase("de"));
/** Words a line never ends with: they lead into what follows. */
const LEADING = new Set(all("leading"));
/** Words a new line begins well with: they open a part; „served“ („served with …“) even more. */
const OPENING: Record<string, number> = { ...Object.fromEntries(all("opening").map((w) => [w, 3])), served: 4 };
/** Prepositions open a part too, a little less clearly. */
const PREPOSITIONS = new Set(all("prepositions"));
const DASHES = /^[–—]$/;
/** A lowercase word with an adjective's ending before a comma: „fester,“. */
const INFLECTED = /^\p{Ll}\p{L}*(e|er|es|en|em),$/u;
const NUMBER = /^\d[\d.,:/–-]*$/;

const bare = (w: string) => w.replace(/^[„“"»«(‚']+|[„“"»«)‚',;:.!?]+$/g, "");
const lower = (w: string) => /^\p{Ll}/u.test(w);
const upper = (w: string) => /^\p{Lu}/u.test(w);

/** What it costs to end a line with `prev` (the word at `at`) and begin the next with `next`. */
export function breakCost(prev: string, next: string, at = 1): number {
  const p = prev.trim();
  const n = next.trim();
  const word = bare(p).toLocaleLowerCase("de");
  // A number stays with what it counts: „13. / März“, „3 / Stunden“, „19 / Uhr“.
  if (NUMBER.test(p) || /^\d+\.,?$/.test(p)) return /,$/.test(p) ? 5 : 15;
  if (n === "·" || n === "|" || n === "/") return 15;
  if (p === "·" || p === "|" || p.endsWith("·")) return 1;
  if (DASHES.test(p) || /[:;]$/.test(p)) return 1;
  // An abbreviation is no end of a sentence: „inkl. / 3 Stunden“, „ca. / 200 g“.
  if (/^\p{Ll}[\p{L}]{0,4}\.$/u.test(p)) return 12;
  if (/[.!?…]["“”»)’]*$/.test(p)) return 0;
  // A comma between two adjectives of one noun is inside it: „mit fester, / karamellisierter Zuckerkruste“.
  if (INFLECTED.test(p) && INFLECTED.test(`${n},`) && !LEADING.has(bare(n).toLocaleLowerCase("de"))) return 10;
  if (/,["“”»)’]*$/.test(p)) return 2;
  if (DASHES.test(n)) return 6;
  // A parenthesis is a part of its own: „Tapas-Bar Pit / (Demo)“.
  if (/^[(\[]/.test(n) || /[)\]]$/.test(p)) return 2;
  // An amount after the name begins a line well: „Rumpsteak vom Grill / 250 g“.
  if (/^\d/.test(n) && /\p{L}$/u.test(p)) return 3;
  if (LEADING.has(word)) return 20;
  const opens = bare(n).toLocaleLowerCase("de");
  if (lower(n) && OPENING[opens] !== undefined) return OPENING[opens]!;
  if (lower(n) && PREPOSITIONS.has(opens)) return 5;
  // German nouns are capitalised: a small word before one leads into it („gefülltes / Schweinefilet“),
  // and so does an adjective that begins the text („Deftiger / Spießbraten“).
  if (upper(n) && lower(p) && /\p{L}$/u.test(p)) return 10;
  if (at === 0 && upper(n) && /^\p{Lu}\p{Ll}+(er|es|en|em|e)$/u.test(p)) return 10;
  return 8;
}

/** What a line's free room costs, `share` its width as a share of the line. */
function roomCost(share: number, last: boolean, mode: BreakMode, words: number): number {
  const free = Math.max(0, 1 - share);
  if (mode === "balance") return 24 * free * free;
  if (!last) return 20 * free * free;
  return share < 0.35 ? 24 * (0.35 - share) + (words === 1 ? 4 : 0) : 0;
}

/**
 * The lines' starts (indexes of the words that begin the second, third … line) for `lines` lines of
 * at most `width`, with the least cost; null when the words do not go into that many lines.
 */
export function semanticPlan(words: readonly BreakWord[], width: number, lines: number, mode: BreakMode = "left"): number[] | null {
  const n = words.length;
  if (lines < 2 || n < lines) return null;
  // The width of the words i … j-1 on one line.
  const widthOf = (i: number, j: number) => {
    let w = words[i]!.width;
    for (let k = i + 1; k < j; k++) w += words[k]!.space + words[k]!.width;
    return w;
  };
  // best[l][j]: the least cost of words 0 … j-1 on l lines, ending with a break before j.
  const best: number[][] = Array.from({ length: lines + 1 }, () => new Array<number>(n + 1).fill(Infinity));
  const from: number[][] = Array.from({ length: lines + 1 }, () => new Array<number>(n + 1).fill(-1));
  best[0]![0] = 0;
  for (let l = 1; l <= lines; l++) {
    const last = l === lines;
    for (let j = l; j <= n; j++) {
      if (last && j !== n) continue;
      for (let i = j - 1; i >= l - 1; i--) {
        if (best[l - 1]![i] === Infinity) continue;
        const w = widthOf(i, j);
        if (w > width + 1e-6) break;
        const cut = j < n ? breakCost(words[j - 1]!.text, words[j]!.text, j - 1) : 0;
        const cost = best[l - 1]![i]! + roomCost(w / width, last, mode, j - i) + cut;
        if (cost < best[l]![j]!) {
          best[l]![j] = cost;
          from[l]![j] = i;
        }
      }
    }
  }
  if (best[lines]![n] === Infinity) return null;
  const starts: number[] = [];
  for (let l = lines, j = n; l > 1; l--) {
    j = from[l]![j]!;
    starts.unshift(j);
  }
  return starts;
}

/** The lines' starts when each line takes as many words as fit: breaking at the width, as Word does. */
export function greedyStarts(words: readonly BreakWord[], width: number): number[] {
  const starts: number[] = [];
  let x = 0;
  words.forEach((w, k) => {
    if (k > 0 && x + w.space + w.width > width) {
      starts.push(k);
      x = w.width;
    } else x += (k > 0 ? w.space : 0) + w.width;
  });
  return starts;
}

/**
 * The text for the net (S58), where the browser breaks the lines: a short word that leads into the
 * next („mit“, „und“, „der“) and a number before what it counts („19 Uhr“) joined to the next word
 * with a protected space, so a line never ends with them. The browser breaks the rest; balancing
 * there (`text-wrap: balance`) would break against the sense: „Tapas-Bar / Pit (Demo)“.
 */
export function glued(text: string): string {
  return text.replace(/(?<=^|[\s(„"»])([\p{L}\d.,:&+]{1,6}) (?=([\p{L}\d„"«(]\S*))/gu, (all, word: string, next: string) => {
    // A number before a word, not before „und“: „19 Uhr“, „3 Stunden“, but „21. / und 27.“ may break.
    const number = /^\d[\d.,:]*$/.test(word) && /^\p{L}/u.test(next) && !LEADING.has(next.toLocaleLowerCase("de"));
    return LEADING.has(word.toLocaleLowerCase("de")) || number ? `${word}\u00a0` : all;
  });
}
